import React, { useState, useEffect } from "react";
import Select from "react-select";

const MultiAsyncDropdown = ({
    label,
    name,
    apiCallFunction,
    placeholder = "Select",
    required = false,
    needSearch = false,
    debounceTime = 300,
    onChange,
    compact = false,
    ...props
}) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [selectedValues, setSelectedValues] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (inputValue && apiCallFunction) {
                fetchOptions(inputValue);
            }
        }, debounceTime);

        return () => clearTimeout(delayDebounce);
    }, [inputValue]);

    useEffect(() => {
        if (props.value) {
            setSelectedValues(props.value);
        }
    }, [props.value]);

    const fetchOptions = async (input) => {
        try {
            setIsLoading(true);
            const response = await apiCallFunction(input);
            if (Array.isArray(response)) {
                setOptions(response);
            } else {
                setOptions([]);
            }
        } catch (error) {
            console.error("Error fetching options:", error);
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (selected) => {
        setSelectedValues(selected || []);
        if (onChange) {
            onChange(selected || []);
        }
    };

    return (
        <div className="form-control w-full">
            {label && (
                <label
                    htmlFor={props.id || props.name}
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <Select
                isMulti
                options={options}
                value={selectedValues}
                onChange={handleChange}
                onInputChange={(value) => setInputValue(value)}
                classNamePrefix="react-select"
                placeholder={placeholder}
                isSearchable={needSearch}
                isLoading={isLoading}
                noOptionsMessage={() => "No options available"}
                styles={{
                    control: (base, state) => ({
                        ...base,
                        minHeight: compact ? '40px' : '44px',
                        borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
                        boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.1)' : 'none',
                        '&:hover': {
                            borderColor: '#9ca3af'
                        },
                        borderRadius: '8px',
                        transition: 'all 0.2s ease'
                    }),
                    valueContainer: (base) => ({
                        ...base,
                        ...(compact ? { paddingTop: 0, paddingBottom: 0 } : {}),
                    }),
                    indicatorsContainer: (base) => ({
                        ...base,
                        ...(compact ? { height: '38px' } : {}),
                    }),
                    menu: (base) => ({
                        ...base,
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e5e7eb'
                    }),
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#f3f4f6' : 'white',
                        color: state.isSelected ? 'white' : '#374151',
                        '&:active': {
                            backgroundColor: '#1d4ed8'
                        }
                    }),
                    multiValue: (base) => ({
                        ...base,
                        backgroundColor: '#e0e7ff',
                        borderRadius: '6px'
                    }),
                    multiValueLabel: (base) => ({
                        ...base,
                        color: '#3730a3',
                        fontWeight: '500'
                    }),
                    multiValueRemove: (base) => ({
                        ...base,
                        color: '#6366f1',
                        '&:hover': {
                            backgroundColor: '#c7d2fe',
                            color: '#4338ca'
                        }
                    }),
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                    }),
                }}
                {...props}
            />
        </div>
    );
};

export default MultiAsyncDropdown;
